// AdminContext — isolated from the user-facing AuthContext.
// Stores the admin JWT under localStorage key 'tdo_admin_token' so it never
// collides with the regular user session.
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'tdo_admin_token';

const AdminAuthContext = createContext(null);

export const AdminAuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => localStorage.getItem(STORAGE_KEY) || null);
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async (t) => {
    try {
      const resp = await axios.get(`${API}/api/admin/me`, {
        headers: { Authorization: `Bearer ${t}` },
        timeout: 8000,
      });
      setAdmin(resp.data);
      return resp.data;
    } catch {
      localStorage.removeItem(STORAGE_KEY);
      setToken(null);
      setAdmin(null);
      return null;
    }
  }, []);

  useEffect(() => {
    const boot = async () => {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) await fetchMe(stored);
      setLoading(false);
    };
    boot();
  }, [fetchMe]);

  const login = useCallback(async (email, senha) => {
    const resp = await axios.post(`${API}/api/admin/auth/login`, { email, senha });
    localStorage.setItem(STORAGE_KEY, resp.data.token);
    setToken(resp.data.token);
    setAdmin(resp.data.admin);
    return resp.data.admin;
  }, []);

  const changePassword = useCallback(async (senha_atual, nova_senha) => {
    await axios.post(
      `${API}/api/admin/auth/change-password`,
      { senha_atual, nova_senha },
      { headers: { Authorization: `Bearer ${token}` } },
    );
    if (token) await fetchMe(token);
  }, [token, fetchMe]);

  const logout = useCallback(async () => {
    try {
      if (token) {
        await axios.post(`${API}/api/admin/auth/logout`, {}, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
    } catch { /* noop */ }
    localStorage.removeItem(STORAGE_KEY);
    setToken(null);
    setAdmin(null);
  }, [token]);

  const isSuper = admin?.role === 'super_admin';

  // refreshMe(optionalToken) — if a token is supplied, adopt it (used by
  // AdminAuthCallback after the Google exchange so the protected routes can
  // see the admin immediately, without forcing a full page reload).
  const refreshMe = useCallback(async (override) => {
    const useToken = override || token;
    if (!useToken) return null;
    if (override && override !== token) {
      localStorage.setItem(STORAGE_KEY, override);
      setToken(override);
    }
    return await fetchMe(useToken);
  }, [token, fetchMe]);

  return (
    <AdminAuthContext.Provider
      value={{ token, admin, loading, login, logout, changePassword, isSuper, refreshMe }}
    >
      {children}
    </AdminAuthContext.Provider>
  );
};

export const useAdminAuth = () => {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error('useAdminAuth must be used inside AdminAuthProvider');
  return ctx;
};

export const adminAxios = (token) =>
  axios.create({
    baseURL: API,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    timeout: 12000,
  });
